import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, Chip, Grid, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

import { useTranslation } from "react-i18next";
import ControlledTextFieldSeparatedLabel from "../../../../components/controlled/ControlledTextFieldSeparatedLabel";
import FlexControlledRadioGroupSeparatedLabel from "../../../../components/controlled/FlexControlledRadioGroupSeparatedLabel";
import { useAdminUpdateTransIntegrationMutation } from "../../integrationsApiSlice";
import { getFormSchema } from "./apiFormSchema";

export const TransApiForm = ({ companyId, status, defaultValues }) => {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const [updateTransIntegration, { isLoading }] =
		useAdminUpdateTransIntegrationMutation();

	const formSchema = getFormSchema(t);
	const {
		handleSubmit,
		control,
		formState: { errors },
		setValue,
		setError,
	} = useForm({
		defaultValues: defaultValues,
		resolver: yupResolver(formSchema),
		shouldFocusError: true,
	});

	useEffect(() => {
		if (Object.keys(errors).length > 0) {
			Object.values(errors).forEach((err) => {
				if (err.message) {
					enqueueSnackbar(err.message, {
						variant: "error",
						preventDuplicate: true,
					});
				}
			});
		}
	}, [errors, enqueueSnackbar]);

	const onSubmit = async ({
		transClientId,
		transClientSecret,
		transApiKey,
		transAuthType,
	}) => {
		await updateTransIntegration({
			companyId,
			transClientId,
			transClientSecret,
			transApiKey,
			transAuthType,
		})
			.unwrap()
			.then((_payload) => {
				enqueueSnackbar(t("updated_client_trans_integration"), {
					variant: "success",
				});
			})
			.catch((error) => {
				if (error?.data?.errors) {
					const errors = error.data.errors;
					Object.entries(errors).forEach(([key, message]) => {
						setError(key);
						enqueueSnackbar(message[0], {
							variant: "error",
							preventDuplicate: true,
						});
					});
				} else {
					enqueueSnackbar(error?.data?.message || t("default_error"), {
						variant: "error",
						preventDuplicate: true,
					});
				}
			});
	};

	return (
		<Box
			component="form"
			autoComplete="off"
			noValidate
			onSubmit={handleSubmit(onSubmit)}
			sx={{ p: 0 }}
		>
			<Grid container spacing={2}>
				<Grid item container size={6}>
					<Grid size={12}>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: "6px",
							}}
						>
							<Typography sx={{ fontWeight: 600 }}>{t("status")}: </Typography>
							{status ? (
								<Chip label={t("integrated")} color="success" />
							) : (
								<Chip label={t("no_integration")} color="secondary" />
							)}
						</Box>
					</Grid>
					<Grid size={12}>
						<ControlledTextFieldSeparatedLabel
							control={control}
							name="transClientId"
							type="text"
							label="Client ID"
							error={!!errors?.transClientId}
							labelXs={6}
							fieldXs={6}
							autoComplete="off transClientId new-password"
							inputProps={{
								autoComplete: "off transClientId new-password",
							}}
						/>
					</Grid>
					<Grid size={12}>
						<ControlledTextFieldSeparatedLabel
							control={control}
							name="transClientSecret"
							type="text"
							label="Client Secret"
							error={!!errors?.transClientSecret}
							labelXs={6}
							fieldXs={6}
							autoComplete="off transClientSecret new-password"
							inputProps={{
								autoComplete: "off transClientSecret new-password",
							}}
						/>
					</Grid>
					<Grid size={12}>
						<ControlledTextFieldSeparatedLabel
							control={control}
							name="transApiKey"
							type="text"
							label="API Key"
							error={!!errors?.transApiKey}
							labelXs={6}
							fieldXs={6}
							autoComplete="off transApiKey new-password"
							inputProps={{
								autoComplete: "off transApiKey new-password",
							}}
						/>
					</Grid>
					<Grid size={12}>
						<FlexControlledRadioGroupSeparatedLabel
							control={control}
							name="transAuthType"
							label={t("login_option")}
							options={[
								{ value: "oauth", label: "OAuth" },
								{ value: "login", label: "Login" },
								{ value: "common", label: "Common" },
							]}
							labelStyles={{ flex: "0 0 50%", justifyContent: "start" }}
							radioStyles={{ py: 1 }}
						/>
					</Grid>
					<Grid size={12}>
						<Box sx={{ display: "flex", justifyContent: "end" }}>
							<Button loading={isLoading} type="submit" variant="contained">
								{t("save")}
							</Button>
						</Box>
					</Grid>
				</Grid>
			</Grid>
		</Box>
	);
};
